<template>
  <b-modal
    v-model="isVisible"
    :title="selectedValue ? '타입 수정' : '타입 추가'"
    size="xl"
    centered
    no-close-on-esc
    no-close-on-backdrop
    no-enforce-focus
    hide-footer
    @hide="onBeforeHide"
  >
    <form-wizard
      ref="refFormWizard"
      color="#7367F0"
      :title="null"
      :subtitle="null"
      shape="square"
      back-button-text="이전"
      next-button-text="다음"
      finish-button-text="저장"
      class="mb-3"
      nav-class="justify-content-center"
      @on-complete="saveAndClose"
      @on-change="(_, next) => { currentTabIndex = next }"
    >
      <!-- Step 1: 환자 조건 설정 -->
      <tab-content
        title="환자 조건 설정"
        :before-change="validateStep1"
      >
        <b-alert v-if="isCondReadonly" show variant="warning" class="mb-1">
          1회차 사전 스크리닝이 시작되어 환자 조건 설정을 수정할 수 없습니다.
        </b-alert>
        <p class="mb-1 text-primary">
          ⓘ 환자 조건이 광범위할 경우 스크리닝 오류가 발생할 수 있습니다.(예시: 1세 이상, 여성)
        </p>
        <!-- 타입 이름 -->
        <b-card class="border">
          <b-row class="align-items-center">
            <b-col
              cols="2"
              class="font-weight-bold"
            >
              타입 이름
            </b-col>
            <b-col>
              <b-form-input
                v-model="form.title"
                placeholder="타입 이름을 입력하세요"
                :disabled="isCondReadonly"
              />
            </b-col>
          </b-row>
        </b-card>

        <!-- 성별 조건 -->
        <b-card class="border">
          <b-row class="align-items-center">
            <b-col
              cols="2"
              class="font-weight-bold"
            >
              성별
            </b-col>
            <b-col>
              <b-form-checkbox
                v-model="form.gender.isUse"
                switch
                inline
                class="custom-control-info"
                :disabled="isCondReadonly"
              >
                {{ form.gender.isUse ? '사용' : '사용안함' }}
              </b-form-checkbox>
            </b-col>
          </b-row>
          <b-row
            v-if="form.gender.isUse"
            class="mt-1"
          >
            <b-col>
              <b-form-radio-group
                v-model="form.gender.value"
                :options="[{ text: '남성', value: 'M' }, { text: '여성', value: 'F' }]"
                :disabled="isCondReadonly"
              />
            </b-col>
          </b-row>
        </b-card>

        <!-- 연령 조건 -->
        <b-card class="border">
          <b-row class="align-items-center">
            <b-col
              cols="2"
              class="font-weight-bold"
            >
              연령
            </b-col>
            <b-col>
              <b-form-checkbox
                v-model="form.age.isUse"
                switch
                inline
                class="custom-control-info"
                :disabled="isCondReadonly"
              >
                {{ form.age.isUse ? '사용' : '사용안함' }}
              </b-form-checkbox>
            </b-col>
          </b-row>
          <b-row
            v-if="form.age.isUse"
            class="mt-1 align-items-center"
          >
            <b-col>
              <div class="d-flex align-items-center">
                <span class="mr-50">만</span>
                <b-form-input
                  v-model.number="form.age.minAge"
                  v-input-type="'number'"
                  placeholder="최소"
                  style="width: 80px;"
                  :disabled="isCondReadonly"
                  @input="clearFieldError('age')"
                  @blur="validateField('age')"
                />
                <span class="mx-50">세 이상 ~ 만</span>
                <b-form-input
                  v-model.number="form.age.maxAge"
                  v-input-type="'number'"
                  placeholder="최대"
                  style="width: 80px;"
                  :disabled="isCondReadonly"
                  @input="clearFieldError('age')"
                  @blur="validateField('age')"
                />
                <span class="ml-50">세 미만</span>
              </div>
              <small
                v-if="fieldErrors['age']"
                class="text-danger"
              >{{ fieldErrors['age'] }}</small>
            </b-col>
          </b-row>
        </b-card>

        <!-- 진단(질병) 조건 -->
        <b-card class="border">
          <b-row class="align-items-center">
            <b-col
              cols="2"
              class="font-weight-bold"
            >
              진단(질병)
            </b-col>
            <b-col>
              <b-form-checkbox
                v-model="form.disease.isUse"
                switch
                inline
                class="custom-control-info"
                :disabled="isCondReadonly"
              >
                {{ form.disease.isUse ? '사용' : '사용안함' }}
              </b-form-checkbox>
            </b-col>
          </b-row>
          <template v-if="form.disease.isUse">
            <b-row class="mt-1 align-items-center">
              <b-col>
                <span class="mr-1">질병군 간 교차 조건</span>
                <b-form-radio-group
                  v-model="form.disease.groupCrossCond"
                  :options="andOrOptions"
                  :disabled="form.disease.groups.length <= 1 || isCondReadonly"
                />
              </b-col>
            </b-row>
            <div
              v-for="(group, gIdx) in form.disease.groups"
              :key="gIdx"
              class="cond-group mt-1"
            >
              <div class="cond-group-header">
                <strong>질병군 {{ gIdx + 1 }}</strong>
                <b-button
                  variant="flat-danger"
                  class="btn-icon p-25"
                  :disabled="isCondReadonly"
                  @click.prevent="removeGroup(form.disease.groups, gIdx)"
                >
                  <feather-icon icon="Trash2Icon" size="16" />
                </b-button>
              </div>
              <table class="cond-table w-100">
                <tbody>
                  <tr>
                    <td class="cond-label">질병군 메모</td>
                    <td>
                      <div class="d-flex align-items-center">
                        <b-form-input
                          v-model="group.memo"
                          placeholder="메모를 입력하세요(예: 고혈압)"
                          class="mr-2"
                          style="width: 50%;"
                          :disabled="isCondReadonly"
                        />
                        <b-form-radio-group
                          v-model="group.include"
                          :options="includeOptions"
                          class="flex-shrink-0"
                          :disabled="isCondReadonly"
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="cond-label">질병코드</td>
                    <td>
                      <div class="d-flex">
                        <div class="flex-grow-1">
                          <b-form-textarea
                            v-model="group.codesStr"
                            placeholder="코드를 쉼표(,)로 구분하여 입력하세요 (코드간 OR 조건 적용)"
                            rows="2"
                            :state="fieldErrors[`disease.${gIdx}`] ? false : null"
                            :disabled="isCondReadonly"
                            @input="clearFieldError(`disease.${gIdx}`)"
                            @blur="validateField('disease', gIdx, group)"
                          />
                          <small v-if="fieldErrors[`disease.${gIdx}`]" class="text-danger">{{ fieldErrors[`disease.${gIdx}`] }}</small>
                        </div>
                        <div class="ml-1 flex-shrink-0">
                          <b-button
                            variant="outline-primary"
                            :disabled="isCondReadonly"
                            @click.prevent="triggerExcelUpload('disease', gIdx)"
                          >
                            엑셀 업로드
                          </b-button>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <b-button
              variant="outline-primary"
              class="mt-50 w-100"
              :disabled="isCondReadonly"
              @click.prevent="addGroup(form.disease.groups, 'disease')"
            >
              + 질병군 추가
            </b-button>
          </template>
        </b-card>

        <!-- 처방 조건 -->
        <b-card class="border">
          <b-row class="align-items-center">
            <b-col
              cols="2"
              class="font-weight-bold"
            >
              처방(약품/수가/재료대/검사)
            </b-col>
            <b-col>
              <b-form-checkbox
                v-model="form.prescription.isUse"
                switch
                inline
                class="custom-control-info"
                :disabled="isCondReadonly"
              >
                {{ form.prescription.isUse ? '사용' : '사용안함' }}
              </b-form-checkbox>
            </b-col>
          </b-row>
          <template v-if="form.prescription.isUse">
            <b-row class="mt-1 align-items-center">
              <b-col>
                <span class="mr-1">처방 그룹 간 교차 조건</span>
                <b-form-radio-group
                  v-model="form.prescription.groupCrossCond"
                  :options="andOrOptions"
                  :disabled="form.prescription.prescriptionGroups.length <= 1 || isCondReadonly"
                />
              </b-col>
            </b-row>

            <div
              v-for="(pGroup, pgIdx) in form.prescription.prescriptionGroups"
              :key="pgIdx"
              class="cond-group mt-1"
            >
              <div class="cond-group-header">
                <strong>처방 그룹 {{ pgIdx + 1 }}</strong>
                <b-button
                  variant="flat-danger"
                  class="btn-icon p-25"
                  :disabled="isCondReadonly"
                  @click.prevent="removePrescriptionGroup(pgIdx)"
                >
                  <feather-icon icon="Trash2Icon" size="16" />
                </b-button>
              </div>
              <table class="cond-table w-100">
                <tbody>
                  <tr>
                    <td class="cond-label">처방군 간 교차 조건</td>
                    <td>
                      <b-form-radio-group
                        v-model="pGroup.itemCrossCond"
                        :options="andOrOptions"
                        :disabled="pGroup.items.length <= 1 || isCondReadonly"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>

              <div class="px-1 pb-1">
                <div
                  v-for="(item, iIdx) in pGroup.items"
                  :key="iIdx"
                  class="cond-group mt-1"
                >
                  <div class="cond-group-header">
                    <strong>처방군 {{ iIdx + 1 }}</strong>
                    <b-button
                      variant="flat-danger"
                      class="btn-icon p-25"
                      :disabled="isCondReadonly"
                      @click.prevent="removeGroup(pGroup.items, iIdx)"
                    >
                      <feather-icon icon="Trash2Icon" size="16" />
                    </b-button>
                  </div>
                  <table class="cond-table w-100">
                    <tbody>
                      <tr>
                        <td class="cond-label">처방군 메모</td>
                        <td>
                          <div class="d-flex align-items-center">
                            <b-form-input
                              v-model="item.memo"
                              placeholder="메모를 입력하세요(예: ARB 계열/단백뇨검사)"
                              class="mr-2"
                              style="width: 50%;"
                              :disabled="isCondReadonly"
                            />
                            <b-form-radio-group
                              v-model="item.include"
                              :options="includeOptions"
                              class="flex-shrink-0"
                              :disabled="isCondReadonly"
                            />
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td class="cond-label">처방코드</td>
                        <td>
                          <div class="d-flex align-items-start">
                            <v-select
                              v-model="item.codeType"
                              :options="codeTypeOptions"
                              :reduce="opt => opt.value"
                              label="text"
                              :clearable="false"
                              :disabled="isCondReadonly"
                              style="width: 160px; background-color: #fff; flex-shrink: 0;"
                            />
                            <div class="flex-grow-1 mx-1">
                              <b-form-textarea
                                v-model="item.codesStr"
                                :placeholder="item.codeType === 'CLAIM' ? '코드를 쉼표(,)로 구분하여 입력하세요(코드간 OR 조건 적용)' : '5자리의 원코드(예: D2280)를 쉼표(,)로 구분하여 입력하세요(코드간 OR 조건 적용)'"
                                rows="2"
                                :state="fieldErrors[`presc.${pgIdx}.${iIdx}`] ? false : null"
                                :disabled="isCondReadonly"
                                @input="clearFieldError(`presc.${pgIdx}.${iIdx}`)"
                                @blur="validateField('presc', pgIdx, iIdx, item)"
                              />
                              <small v-if="fieldErrors[`presc.${pgIdx}.${iIdx}`]" class="text-danger">{{ fieldErrors[`presc.${pgIdx}.${iIdx}`] }}</small>
                            </div>
                            <div class="flex-shrink-0">
                              <b-button
                                variant="outline-primary"
                                :disabled="isCondReadonly"
                                @click.prevent="triggerExcelUpload('prescription', pgIdx, iIdx)"
                              >
                                엑셀 업로드
                              </b-button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <b-button
                  variant="outline-primary"
                  class="mt-50 w-100"
                  :disabled="isCondReadonly"
                  @click.prevent="addPrescriptionItem(pgIdx)"
                >
                  + 처방군 추가
                </b-button>
              </div>
            </div>

            <b-button
              variant="outline-primary"
              class="mt-1 w-100"
              :disabled="isCondReadonly"
              @click.prevent="addPrescriptionGroup"
            >
              + 처방 그룹 추가
            </b-button>
          </template>
        </b-card>

        <!-- 검사 결과 조건 -->
        <b-card class="border">
          <b-row class="align-items-center">
            <b-col
              cols="2"
              class="font-weight-bold"
            >
              검사 결과
            </b-col>
            <b-col>
              <b-form-checkbox
                v-model="form.lab.isUse"
                switch
                inline
                class="custom-control-info"
                :disabled="isCondReadonly"
              >
                {{ form.lab.isUse ? '사용' : '사용안함' }}
              </b-form-checkbox>
            </b-col>
          </b-row>
          <template v-if="form.lab.isUse">
            <b-row class="mt-1 align-items-center">
              <b-col>
                <span class="mr-1">검사 결과군 간 교차 조건</span>
                <b-form-radio-group
                  v-model="form.lab.groupCrossCond"
                  :options="andOrOptions"
                  :disabled="form.lab.groups.length <= 1 || isCondReadonly"
                />
              </b-col>
            </b-row>

            <div
              v-for="(group, gIdx) in form.lab.groups"
              :key="gIdx"
              class="cond-group mt-1"
            >
              <div class="cond-group-header">
                <strong>검사 결과군 {{ gIdx + 1 }}</strong>
                <b-button
                  variant="flat-danger"
                  class="btn-icon p-25"
                  :disabled="isCondReadonly"
                  @click.prevent="removeGroup(form.lab.groups, gIdx)"
                >
                  <feather-icon icon="Trash2Icon" size="16" />
                </b-button>
              </div>
              <table class="cond-table w-100">
                <tbody>
                  <tr>
                    <td class="cond-label">검사 결과군 메모</td>
                    <td>
                      <b-form-input
                        v-model="group.memo"
                        placeholder="메모를 입력하세요(예: AST 50이상)"
                        :disabled="isCondReadonly"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td class="cond-label">검사코드</td>
                    <td>
                      <div class="d-flex">
                        <div class="flex-grow-1">
                          <b-form-textarea
                            v-model="group.codesStr"
                            placeholder="5자리의 원코드(예: D2280)를 쉼표(,)로 구분하여 입력하세요(코드간 OR 조건 적용)"
                            rows="2"
                            :state="fieldErrors[`lab.${gIdx}.code`] ? false : null"
                            :disabled="isCondReadonly"
                            @input="clearFieldError(`lab.${gIdx}.code`, `lab.${gIdx}.value`)"
                            @blur="validateField('lab-code', gIdx, group)"
                          />
                          <small v-if="fieldErrors[`lab.${gIdx}.code`]" class="text-danger">{{ fieldErrors[`lab.${gIdx}.code`] }}</small>
                        </div>
                        <div class="ml-1 flex-shrink-0">
                          <b-button
                            variant="outline-primary"
                            :disabled="isCondReadonly"
                            @click.prevent="triggerExcelUpload('lab', gIdx)"
                          >
                            엑셀 업로드
                          </b-button>
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="cond-label">결과 수치 범위</td>
                    <td>
                      <div class="d-flex align-items-center">
                        <b-form-input
                          v-model.number="group.minValue"
                          v-input-type="'number'"
                          placeholder="최소"
                          style="width: 100px;"
                          :state="fieldErrors[`lab.${gIdx}.value`] ? false : null"
                          :disabled="isCondReadonly"
                          @input="clearFieldError(`lab.${gIdx}.value`)"
                          @blur="validateField('lab-value', gIdx, group)"
                        />
                        <span class="mx-50">이상 ~</span>
                        <b-form-input
                          v-model.number="group.maxValue"
                          v-input-type="'number'"
                          placeholder="최대"
                          style="width: 100px;"
                          :state="fieldErrors[`lab.${gIdx}.value`] ? false : null"
                          :disabled="isCondReadonly"
                          @input="clearFieldError(`lab.${gIdx}.value`)"
                          @blur="validateField('lab-value', gIdx, group)"
                        />
                        <span class="ml-50">미만</span>
                      </div>
                      <small
                        v-if="fieldErrors[`lab.${gIdx}.value`]"
                        class="text-danger"
                      >{{ fieldErrors[`lab.${gIdx}.value`] }}</small>
                    </td>
                  </tr>
                  <tr>
                    <td class="cond-label">복수 데이터<br>처리 기준</td>
                    <td>
                      <b-form-radio-group
                        v-model="group.multiMatch"
                        :options="multiMatchOptions"
                        :disabled="isCondReadonly"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <b-button
              variant="outline-primary"
              class="mt-50 w-100"
              :disabled="isCondReadonly"
              @click.prevent="addGroup(form.lab.groups, 'lab')"
            >
              + 검사 결과군 추가
            </b-button>
          </template>
        </b-card>
      </tab-content>

      <!-- Step 2: 알림 팝업 설정 -->
      <tab-content title="알림 팝업 설정">
        <b-card class="border">
          <b-form-group label="알림 유지 시간">
            <v-select
              v-model="form.popupDelaySec"
              :options="delaySecOptions"
              :reduce="opt => opt.value"
              label="text"
              style="width: 200px;"
            />
          </b-form-group>
          <b-form-group label="알림창 내용">
            <validation-observer ref="step2Rules">
              <validation-provider
                #default="{ errors }"
                name="알림창 내용"
                rules="required|popupContent"
              >
                <tinymce-editor
                  v-if="currentTabIndex === 1"
                  v-model="form.popupContent"
                  :is-table="true"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </validation-observer>
          </b-form-group>
        </b-card>
      </tab-content>

      <template #footer="{ activeTabIndex, isLastStep, nextTab, prevTab }">
        <div class="d-flex justify-content-between w-100">
          <b-button
            v-if="activeTabIndex > 0"
            variant="outline-secondary"
            @click="prevTab"
          >
            이전
          </b-button>
          <div v-else />
          <b-button
            v-if="!isLastStep"
            variant="primary"
            :disabled="!hasAnyConditionOn"
            @click="nextTab"
          >
            다음
          </b-button>
          <b-button
            v-else
            variant="primary"
            @click="saveAndClose"
          >
            저장
          </b-button>
        </div>
      </template>
    </form-wizard>

    <!-- 엑셀 업로드 hidden input -->
    <input
      ref="excelInput"
      type="file"
      accept=".xlsx,.xls"
      style="display:none;"
      @change="onExcelFileChange"
    >
  </b-modal>
</template>

<script>
import { ref, computed, watch, getCurrentInstance } from '@vue/composition-api'
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import vSelect from 'vue-select'
import { BAlert } from 'bootstrap-vue'
import TinymceEditor from '@/views/TinyMCE.vue'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate'

export default {
  components: {
    FormWizard,
    TabContent,
    vSelect,
    BAlert,
    TinymceEditor,
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    showPatientTypeSelect: { type: Boolean, default: false },
    selectedValue: { type: Object, default: null },
    isCondReadonly: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const instance = getCurrentInstance()
    const bvModal = instance.proxy.$bvModal
    const refs = instance.proxy.$refs
    const toast = useToast()

    const isVisible = ref(false)
    const currentTabIndex = ref(0)
    const isSaving = ref(false)
    const originalForm = ref(null)

    const initForm = () => ({
      id: null,
      title: '',
      popupDelaySec: 10,
      popupContent: '<table style="width: 270px; max-width: 270px; height: 160px; max-height: 160px;"><tbody><tr><td></td></tr></tbody></table>',
      gender: { isUse: false, value: 'M' },
      age: { isUse: false, minAge: null, maxAge: null },
      disease: {
        isUse: false,
        groupCrossCond: 'AND',
        groups: [],
      },
      prescription: {
        isUse: false,
        groupCrossCond: 'AND',
        prescriptionGroups: [],
      },
      lab: {
        isUse: false,
        groupCrossCond: 'AND',
        groups: [],
      },
    })

    const form = ref(initForm())

    const includeOptions = [
      { text: '포함(진단받은 환자)', value: true },
      { text: '제외(진단받지 않은 환자)', value: false },
    ]

    const codeTypeOptions = [
      { text: '청구코드', value: 'CLAIM' },
      { text: '검사코드', value: 'LAB' },
    ]

    const multiMatchOptions = [
      { text: '최근값', value: 'LAST' },
      { text: '최대값', value: 'MAX' },
      { text: '최소값', value: 'MIN' },
      { text: '평균값', value: 'AVG' },
    ]

    const delaySecOptions = [
      { text: '10초', value: 10 },
      { text: '30초', value: 30 },
      { text: '1분', value: 60 },
      { text: '5분', value: 300 },
      { text: '10분', value: 600 },
      { text: '30분', value: 1800 },
    ]

    const andOrOptions = [{ text: 'AND', value: 'AND' }, { text: 'OR', value: 'OR' }]

    watch(() => props.showPatientTypeSelect, val => { isVisible.value = val; if (val) isSaving.value = false })
    watch(isVisible, val => { if (!val) emit('close') })

    watch(() => form.value.disease.isUse, val => {
      if (val && form.value.disease.groups.length === 0) form.value.disease.groups.push(createDiseaseGroup())
    })
    watch(() => form.value.prescription.isUse, val => {
      if (val && form.value.prescription.prescriptionGroups.length === 0) form.value.prescription.prescriptionGroups.push(createPrescriptionGroup())
    })
    watch(() => form.value.lab.isUse, val => {
      if (val && form.value.lab.groups.length === 0) form.value.lab.groups.push(createLabGroup())
    })

    watch(() => props.selectedValue, val => {
      if (val) {
        form.value = parseDslToForm(val)
      } else {
        form.value = initForm()
      }
      originalForm.value = JSON.parse(JSON.stringify(form.value))
      if (refs.refFormWizard) refs.refFormWizard.reset()
    }, { immediate: true })

    const hasAnyConditionOn = computed(() => (
      form.value.gender.isUse
      || form.value.age.isUse
      || form.value.disease.isUse
      || form.value.prescription.isUse
      || form.value.lab.isUse
    ))

    // 유효성
    const ageError = computed(() => {
      const { isUse, minAge, maxAge } = form.value.age
      if (!isUse) return null
      const min = (minAge == null || minAge === '') ? null : minAge
      const max = (maxAge == null || maxAge === '') ? null : maxAge
      if (min == null && max == null) return '[연령]는(은) 필수 입력입니다.'
      if (min != null && max != null && min >= max) return '최소 값은 최대 값보다 클 수 없습니다.'
      return null
    })

    const labValueError = group => {
      const min = group.minValue === '' ? null : group.minValue
      const max = group.maxValue === '' ? null : group.maxValue
      if (min == null && max == null) return '[결과 수치 범위]는(은) 필수 입력입니다.'
      if (min != null && max != null && min >= max) return '최소 값은 최대 값보다 클 수 없습니다.'
      return null
    }

    const fieldErrors = ref({})
    const clearFieldError = (...keys) => {
      const cleared = {}
      keys.forEach(k => { cleared[k] = null })
      fieldErrors.value = { ...fieldErrors.value, ...cleared }
    }

    const validateField = (type, ...args) => {
      const resolve = () => {
        if (type === 'age') return { key: 'age', error: ageError.value }
        if (type === 'disease') {
          const [gIdx, group] = args
          return { key: `disease.${gIdx}`, error: !group.codesStr?.trim() ? '[질병코드]는(은) 필수 입력입니다.' : null }
        }
        if (type === 'presc') {
          const [pgIdx, iIdx, item] = args
          return { key: `presc.${pgIdx}.${iIdx}`, error: !item.codesStr?.trim() ? '[처방코드]는(은) 필수 입력입니다.' : null }
        }
        if (type === 'lab-code') {
          const [gIdx, group] = args
          return { key: `lab.${gIdx}.code`, error: !group.codesStr?.trim() ? '[검사코드]는(은) 필수 입력입니다.' : null }
        }
        if (type === 'lab-value') {
          const [gIdx, group] = args
          return { key: `lab.${gIdx}.value`, error: labValueError(group) }
        }
        return {}
      }
      const { key, error } = resolve()
      fieldErrors.value = { ...fieldErrors.value, [key]: error || null }
      return !!error
    }

    extend('popupContent', {
      validate: value => {
        if (value !== null) {
          const val = value.substring(value.indexOf('<td>') + 4, value.indexOf('</td>')).replace(/&nbsp;/g, '')
          if (val.trim().length === 0) return '[{_field_}]는(은) 필수 입력입니다.'
        }
        return true
      },
    })

    const validateStep1 = () => {
      if (props.isCondReadonly) return true
      let valid = true
      if (form.value.age.isUse && validateField('age')) valid = false
      if (form.value.disease.isUse) {
        form.value.disease.groups.forEach((g, i) => { if (validateField('disease', i, g)) valid = false })
      }
      if (form.value.prescription.isUse) {
        form.value.prescription.prescriptionGroups.forEach((pg, pgIdx) => {
          pg.items.forEach((item, iIdx) => { if (validateField('presc', pgIdx, iIdx, item)) valid = false })
        })
      }
      if (form.value.lab.isUse) {
        form.value.lab.groups.forEach((g, i) => {
          if (validateField('lab-code', i, g)) valid = false
          if (validateField('lab-value', i, g)) valid = false
        })
      }
      return valid
    }

    const validateStep2 = () => refs.step2Rules.validate()

    // 그룹 추가/삭제
    const createDiseaseGroup = () => ({ memo: '', codesStr: '', include: true })
    const createLabGroup = () => ({ memo: '', codesStr: '', minValue: null, maxValue: null, multiMatch: 'LAST' })
    const createPrescriptionItem = () => ({ memo: '', codeType: 'CLAIM', codesStr: '', include: true })
    const createPrescriptionGroup = () => ({ itemCrossCond: 'AND', items: [createPrescriptionItem()] })

    const addGroup = (arr, type) => {
      if (type === 'disease') arr.push(createDiseaseGroup())
      else if (type === 'lab') arr.push(createLabGroup())
    }
    const removeGroup = (arr, idx) => arr.splice(idx, 1)
    const addPrescriptionGroup = () => form.value.prescription.prescriptionGroups.push(createPrescriptionGroup())
    const removePrescriptionGroup = idx => form.value.prescription.prescriptionGroups.splice(idx, 1)
    const addPrescriptionItem = pgIdx => form.value.prescription.prescriptionGroups[pgIdx].items.push(createPrescriptionItem())

    // 엑셀 업로드
    const excelTarget = ref(null)

    const triggerExcelUpload = (category, ...args) => {
      excelTarget.value = { category, args }
      refs.excelInput.value = ''
      refs.excelInput.click()
    }

    const onExcelFileChange = async e => {
      const file = e.target.files[0]
      if (!file) return
      try {
        const XLSX = await import('xlsx')
        const data = await file.arrayBuffer()
        const wb = XLSX.read(data)
        const ws = wb.Sheets[wb.SheetNames[0]]
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1 })
        const codes = [...new Set(rows.flat().map(c => String(c).trim()).filter(Boolean))]
        const joined = codes.join(',')
        const { category, args } = excelTarget.value
        if (category === 'disease') {
          const [gIdx] = args
          form.value.disease.groups[gIdx].codesStr = mergeCodesStr(form.value.disease.groups[gIdx].codesStr, joined)
        } else if (category === 'prescription') {
          const [pgIdx, iIdx] = args
          form.value.prescription.prescriptionGroups[pgIdx].items[iIdx].codesStr = mergeCodesStr(
            form.value.prescription.prescriptionGroups[pgIdx].items[iIdx].codesStr, joined,
          )
        } else if (category === 'lab') {
          const [gIdx] = args
          form.value.lab.groups[gIdx].codesStr = mergeCodesStr(form.value.lab.groups[gIdx].codesStr, joined)
        }
      } catch {
        toast({ component: ToastificationContent, props: { title: '엑셀 파일 처리 중 오류가 발생했습니다.', icon: 'AlertTriangleIcon', variant: 'danger' } })
      }
    }

    const mergeCodesStr = (existing, newCodes) => {
      const set = new Set([
        ...existing.split(',').map(s => s.trim()).filter(Boolean),
        ...newCodes.split(',').map(s => s.trim()).filter(Boolean),
      ])
      return [...set].join(',')
    }

    // DSL 빌드
    const buildDsl = f => {
      const dsl = {
        dsl_version: '1.0', subject_filter: {}, groups: [], expression: null,
      }

      // subject_filter (미설정 값은 null로 저장)
      dsl.subject_filter.gender = f.gender.isUse ? f.gender.value : null
      dsl.subject_filter.age_min = f.age.isUse ? (f.age.minAge ?? null) : null
      dsl.subject_filter.age_max = f.age.isUse ? (f.age.maxAge ?? null) : null

      const expressionOperands = []

      // diagnosis
      if (f.disease.isUse && f.disease.groups.length > 0) {
        const subGroups = f.disease.groups.map((g, i) => ({
          sub_group_id: `diag1_sub${i + 1}`,
          memo: g.memo || '',
          include: g.include,
          codes: parseCodes(g.codesStr),
          code_match: 'EXACT',
        }))
        dsl.groups.push({
          group_id: 'diag1',
          category: 'diagnosis',
          sub_combine_op: f.disease.groupCrossCond,
          sub_groups: subGroups,
        })
        expressionOperands.push({ type: 'group_ref', group_id: 'diag1' })
      }

      // prescription
      if (f.prescription.isUse && f.prescription.prescriptionGroups.length > 0) {
        const prescOps = []
        f.prescription.prescriptionGroups.forEach((pg, pgIdx) => {
          const groupId = `presc${pgIdx + 1}`
          const subGroups = pg.items.map((item, iIdx) => ({
            sub_group_id: `${groupId}_sub${iIdx + 1}`,
            memo: item.memo || '',
            include: item.include,
            code_type: item.codeType,
            codes: parseCodes(item.codesStr),
            code_match: item.codeType === 'LAB' ? 'PREFIX' : 'EXACT',
          }))
          dsl.groups.push({
            group_id: groupId,
            category: 'prescription',
            sub_combine_op: pg.itemCrossCond,
            sub_groups: subGroups,
          })
          prescOps.push({ type: 'group_ref', group_id: groupId })
        })
        if (prescOps.length === 1) {
          expressionOperands.push(prescOps[0])
        } else {
          expressionOperands.push({ type: 'operator', op: f.prescription.groupCrossCond, operands: prescOps })
        }
      }

      // lab
      if (f.lab.isUse && f.lab.groups.length > 0) {
        const subGroups = f.lab.groups.map((g, i) => ({
          sub_group_id: `lab1_sub${i + 1}`,
          memo: g.memo || '',
          include: true,
          codes: parseCodes(g.codesStr),
          code_match: 'PREFIX',
          value_range: {
            min: g.minValue ?? null,
            max: g.maxValue ?? null,
            multi_match: g.multiMatch,
          },
        }))
        dsl.groups.push({
          group_id: 'lab1',
          category: 'lab',
          sub_combine_op: f.lab.groupCrossCond,
          sub_groups: subGroups,
        })
        expressionOperands.push({ type: 'group_ref', group_id: 'lab1' })
      }

      if (expressionOperands.length === 1) {
        dsl.expression = expressionOperands[0]
      } else if (expressionOperands.length > 1) {
        dsl.expression = { type: 'operator', op: 'AND', operands: expressionOperands }
      }

      return dsl
    }

    // conditionLabels 생성
    const buildConditionLabels = f => {
      const parts = []
      if (f.gender.isUse) parts.push(`[성별 - ${f.gender.value === 'M' ? '남자' : '여자'}]`)
      if (f.age.isUse) {
        const min = f.age.minAge != null ? `만 ${f.age.minAge}세 이상` : ''
        const max = f.age.maxAge != null ? `${f.age.maxAge}세 미만` : ''
        parts.push(`[연령 - ${[min, max].filter(Boolean).join(' ')}]`)
      }
      if (f.disease.isUse) {
        f.disease.groups.forEach(g => {
          parts.push(`[진단 - ${g.memo || ''}]`)
        })
      }
      if (f.prescription.isUse) {
        f.prescription.prescriptionGroups.forEach(pg => {
          pg.items.forEach(item => {
            parts.push(`[처방 - ${item.memo || ''}]`)
          })
        })
      }
      if (f.lab.isUse) {
        f.lab.groups.forEach(g => {
          parts.push(`[검사결과 - ${g.memo || ''}]`)
        })
      }
      return parts.join(' ')
    }

    // DSL → form 역변환 (수정 모드)
    const parseDslToForm = cond => {
      const f = initForm()
      f.id = cond.id ?? null
      f.title = cond.title ?? ''
      f.popupDelaySec = cond.popupDelaySec ?? 10
      f.popupContent = cond.popupContent ?? ''

      const dsl = typeof cond.conditions === 'string' ? JSON.parse(cond.conditions) : cond.conditions
      if (!dsl) return f

      // subject_filter
      if (dsl.subject_filter) {
        if (dsl.subject_filter.gender) { f.gender.isUse = true; f.gender.value = dsl.subject_filter.gender }
        if (dsl.subject_filter.age_min != null || dsl.subject_filter.age_max != null) {
          f.age.isUse = true
          f.age.minAge = dsl.subject_filter.age_min
          f.age.maxAge = dsl.subject_filter.age_max
        }
      }

      // groups
      if (dsl.groups) {
        const diagGroup = dsl.groups.find(g => g.category === 'diagnosis')
        if (diagGroup) {
          f.disease.isUse = true
          f.disease.groupCrossCond = diagGroup.sub_combine_op || 'AND'
          f.disease.groups = diagGroup.sub_groups.map(sg => ({
            memo: sg.memo || '',
            codesStr: (sg.codes || []).join(','),
            include: sg.include,
          }))
        }

        const prescGroups = dsl.groups.filter(g => g.category === 'prescription')
        if (prescGroups.length > 0) {
          f.prescription.isUse = true
          f.prescription.prescriptionGroups = prescGroups.map(pg => ({
            itemCrossCond: pg.sub_combine_op || 'AND',
            items: pg.sub_groups.map(sg => ({
              memo: sg.memo || '',
              codeType: sg.code_type || 'CLAIM',
              codesStr: (sg.codes || []).join(','),
              include: sg.include,
            })),
          }))
          // expression에서 처방 그룹 간 교차 조건 추출
          if (dsl.expression) {
            const prescNode = findPrescNode(dsl.expression)
            if (prescNode && prescNode.op) f.prescription.groupCrossCond = prescNode.op
          }
        }

        const labGroup = dsl.groups.find(g => g.category === 'lab')
        if (labGroup) {
          f.lab.isUse = true
          f.lab.groupCrossCond = labGroup.sub_combine_op || 'AND'
          f.lab.groups = labGroup.sub_groups.map(sg => ({
            memo: sg.memo || '',
            codesStr: (sg.codes || []).join(','),
            minValue: sg.value_range?.min ?? null,
            maxValue: sg.value_range?.max ?? null,
            multiMatch: sg.value_range?.multi_match || 'LAST',
          }))
        }
      }

      return f
    }

    const findPrescNode = node => {
      if (!node) return null
      if (node.operands) {
        const prescRefs = node.operands.filter(o => o.type === 'group_ref' && o.group_id?.startsWith('presc'))
        if (prescRefs.length > 1) return node
        for (const o of node.operands) {
          const found = findPrescNode(o)
          if (found) return found
        }
      }
      return null
    }

    const parseCodes = str => (str || '').split(',').map(s => s.trim().toUpperCase()).filter(Boolean)

    // 저장
    const saveAndClose = async () => {
      const valid = await validateStep2()
      if (!valid) return

      const conditions = JSON.stringify(buildDsl(form.value))
      const conditionLabels = buildConditionLabels(form.value)

      const result = {
        id: form.value.id,
        title: form.value.title,
        conditions,
        conditionLabels,
        popupDelaySec: form.value.popupDelaySec,
        popupContent: form.value.popupContent,
      }

      isSaving.value = true
      form.value = initForm()
      if (refs.refFormWizard) refs.refFormWizard.reset()
      currentTabIndex.value = 0

      emit('close', result)
    }

    const onBeforeHide = evt => {
      if (isSaving.value) return
      const dirty = JSON.stringify(form.value) !== JSON.stringify(originalForm.value)
      if (!dirty) {
        form.value = initForm()
        if (refs.refFormWizard) refs.refFormWizard.reset()
        emit('close')
        return
      }
      evt.preventDefault()
      bvModal.msgBoxConfirm('작성 중인 내용이 저장되지 않습니다. 닫으시겠습니까?', {
        size: 'sm', cancelVariant: 'outline-secondary', centered: true, okTitle: '확인', cancelTitle: '취소',
      }).then(confirmed => {
        if (confirmed) {
          form.value = initForm()
          if (refs.refFormWizard) refs.refFormWizard.reset()
          currentTabIndex.value = 0
          isSaving.value = true
          isVisible.value = false
          emit('close')
        }
      })
    }

    return {
      isVisible,
      currentTabIndex,
      form,
      hasAnyConditionOn,
      ageError,
      fieldErrors,
      clearFieldError,
      validateField,
      labValueError,
      includeOptions,
      andOrOptions,
      codeTypeOptions,
      multiMatchOptions,
      delaySecOptions,
      addGroup,
      removeGroup,
      addPrescriptionGroup,
      removePrescriptionGroup,
      addPrescriptionItem,
      triggerExcelUpload,
      onExcelFileChange,
      saveAndClose,
      validateStep1,
      validateStep2,
      onBeforeHide,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-wizard.scss';

.vue-form-wizard {
  .wizard-nav.wizard-nav-pills {
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    width: 100% !important;

    li {
      margin: 0 15px;
    }
  }
}

/* 조건 그룹 카드 공통 스타일 */
.cond-group {
  border: 1px solid #ebe9f1;
  border-radius: 6px;
}

.cond-group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f3f2f7;
  padding: 7px 12px;
  border-bottom: 1px solid #ebe9f1;
  border-radius: 6px 6px 0 0;
}

.cond-table {
  background-color: #f8f9fa;
  border-collapse: collapse;

  tbody tr td {
    border: none !important;
    padding: 10px 14px !important;
    vertical-align: middle;
  }

  tbody tr + tr {
    border-top: 1px solid #e9ecef !important;
  }
}

.cond-label {
  width: 130px;
  min-width: 130px;
  font-weight: 500;
  color: #5e5873;
  background-color: #f8f9fa;
}

.cond-table .vs__dropdown-toggle {
  background-color: #fff;
}
</style>
